import React, { useState, useEffect, useRef } from 'react';
import style from './BodyMassWrite.module.css';
import HeadSub from '../../../../components/comm/HeadSub';
import Modal from '../../../../components/comm/Modal';
import CustomDatePicker from '../../../../components/datepicker/CustomDatePicker';
import CustomTimePicker from '../../../../components/datepicker/CustomTimePicker';
import BottomSheetBase from '../../../../components/comm/BottomSheetBase';
import URL from "../../../../constants/url";
import ToastPopup from '../../../../components/comm/ToastPopup';
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';

import axiosApi from '../../../../api/daonAxios';

const calculateBMI = (height, weight) => {
    const bmi = Number(
        (weight / ((height / 100) * (height / 100))).toFixed(2)
    );

    return bmi;
};

/*
저체중: 18.5 미만            :: L
정상: 18.5 이상 ~ 23 미만    :: N
주의: 23 이상 ~ 25 미만      :: C
위험: 25 이상                :: D
*/
const getRsltCd = (bmi) => {
    if (bmi < 18.5) {
        return "L"; // Low
    }
    if (bmi < 23) {
        return "N"; // Normal
    }
    if (bmi < 25) {
        return "C"; // Caution
    }
    return "D"; // Danger
};

const BodyMassWrite = () => {
    const navigate = useNavigate();

    const [recordDate, setRecordDate] = useState('');
    const [openRecordDate, setOpenRecordDate] = useState(false);
    const [recordTime, setRecordTime] = useState('');
    const [openRecordTime, setOpenRecordTime] = useState(false);
    const [time, setTime] = useState({
        hour: '',
        minute: '',
        second: ''
    });
    const [toast, setToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [stature, setStature] = useState('');
    const [weight, setWeight] = useState('');

    const isSendingRef = useRef(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTimeOpen, setModalTimeOpen] = useState(false);

    const focusDateInputRef = useRef(null);
    const focusTimeInputRef = useRef(null);
    const dayRef = useRef(null);
    const timeRef = useRef(null);
    const statureRef = useRef(null);
    const weightRef = useRef(null);

    const [dateInputFocused, setDateInputFocused] = useState(false);
    const [timeInputFocused, setTimeInputFocused] = useState(false);

    const handleDateConfirm = (date) => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const picked = new Date(date); picked.setHours(0, 0, 0, 0);
        if (picked > today) { setOpenRecordDate(false); setModalOpen(true); setDateInputFocused(true); return; }
        setRecordDate(picked); setOpenRecordDate(false); setDateInputFocused(false);
        setTimeout(() => { dayRef.current?.focus({ preventScroll: true }); dayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 200);
    };

    const closeModal = () => {
        setModalOpen(false);
        setTimeout(() => { focusDateInputRef.current?.focus({ preventScroll: true }); focusDateInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 200);
        setOpenRecordDate(true); setDateInputFocused(true);
    };

    const closeModalTime = () => {
        setModalTimeOpen(false);
        setTimeout(() => { focusTimeInputRef.current?.focus({ preventScroll: true }); focusTimeInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 200);
        setOpenRecordTime(true); setTimeInputFocused(true);
    };

    const handleConfirmTime = (value) => {
        const now = new Date();
        const selectedDateTime = new Date(recordDate || now);

        const hour = Number(value.hour);
        const minute = Number(value.minute);
        const second = Number(value.second ?? 0);

        selectedDateTime.setHours(hour, minute, second, 0);

        if (selectedDateTime > now) {
            setOpenRecordTime(false);
            setTimeout(() => setModalTimeOpen(true), 350);
            return;
        }

        setTime({
            hour: String(hour).padStart(2, '0'),
            minute: String(minute).padStart(2, '0'),
            second: String(second).padStart(2, '0')
        });

        const period = hour < 12 ? '오전' : '오후';
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;

        setRecordTime(
            `${period} ${hour12}:${String(minute).padStart(2, '0')}`
        );

        setOpenRecordTime(false);
        setTimeInputFocused(false);

        setTimeout(() => {
            timeRef.current?.focus({ preventScroll: true });
            timeRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }, 200);
    };

    useEffect(() => {
        const now = new Date();

        setRecordDate(now);

        const hour = now.getHours();
        const minute = now.getMinutes();
        const second = now.getSeconds();

        setTime({
            hour: String(hour).padStart(2, '0'),
            minute: String(minute).padStart(2, '0'),
            second: String(second).padStart(2, '0')
        });

        const period = hour < 12 ? '오전' : '오후';
        const hour12 = hour % 12 === 0 ? 12 : hour % 12;

        setRecordTime(
            `${period} ${hour12}:${String(minute).padStart(2, '0')}`
        );

        // 기존 퍼블리싱용 키 강제 세팅 제거
        // setStature('179.2');
    }, []);

    const handleComplete = async () => {
        const heightNumber = Number(stature);
        const weightNumber = Number(weight);

        if (!stature) {
            setToastMessage('키를 입력해주세요.');
            setToast(true);
            return;
        }

        if (heightNumber <= 0) {
            setToastMessage('0cm보다 큰 키를 입력해주세요.');
            setToast(true);
            return;
        }

        if (!weight) {
            setToastMessage('체중을 입력해주세요.');
            setToast(true);
            return;
        }

        if (weightNumber <= 0) {
            setToastMessage('0kg보다 큰 체중을 입력해주세요.');
            setToast(true);
            return;
        }

        if (heightNumber > 999.9) {
            setToastMessage("키는 999.9cm 이하만 입력 가능합니다.");
            setToast(true);
            return;
        }

        if (weightNumber > 999.9) {
            setToastMessage("체중은 999.9kg 이하만 입력 가능합니다.");
            setToast(true);
            return;
        }

        if (!recordDate) {
            setToastMessage('날짜를 선택해주세요.');
            setToast(true);
            return;
        }

        if (!recordTime) {
            setToastMessage('시간을 선택해주세요.');
            setToast(true);
            return;
        }

        const bmi = calculateBMI(heightNumber, weightNumber);
        const rsltCd = getRsltCd(Number(bmi));

        if (bmi > 999.99) {
            setToastMessage("BMI 계산 결과가 허용 범위를 초과했습니다.");
            setToast(true);
            return;
        }

        if (isSendingRef.current) {
            return;
        }

        isSendingRef.current = true;

        try {
            const hour = String(time.hour).padStart(2, '0');
            const minute = String(time.minute).padStart(2, '0');
            const second = String(time.second ?? '00').padStart(2, '0');

            const saveObj = {
                recDe: dayjs(recordDate).format('YYYYMMDD'),
                recTm: `${hour}${minute}${second}`,
                bmi: calculateBMI(heightNumber, weightNumber),
                height: heightNumber,
                bdwgh: weightNumber,
                rsltCd: rsltCd
            };

            await saveBmi(saveObj);

            navigate(URL.BODY_MASS_RECORD, {
                replace: true,
                state: {
                    showToast: true,
                    toastMessage: '기록 완료!'
                }
            });
        } catch (error) {
            setToastMessage('기록 저장에 실패했습니다.');
            setToast(true);
        } finally {
            isSendingRef.current = false;
        }
    };

    const handleDismissDate = () => {
        setOpenRecordDate(false); setDateInputFocused(false);
        setTimeout(() => { dayRef.current?.focus({ preventScroll: true }); dayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 200);
    };

    const handleDismissTime = () => {
        setOpenRecordTime(false); setTimeInputFocused(false);
        setTimeout(() => { timeRef.current?.focus({ preventScroll: true }); timeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 200);
    };

    //////////////////////////////////////////

    const [saveObj, setSaveObj] = useState({
        recDe: '',
        recTm: '',
        bmi: null,
        height: null,
        bdwgh: null
    });

    const saveBmi = async (saveObj) => {

        const jToken = localStorage.getItem('jToken');

        const requestOption = {
            headers: {
                "Authorization": `Bearer ${jToken}`,
            },
        };

        const response = await axiosApi.post('/bmi', saveObj, requestOption);

        console.log("체질량 등록 : " + response.data.result);
    }

    return (
        <>
            <HeadSub title="체질량 기록하기" />

            <div id="sub-content" data-layout="white">
                <div className="inner-wrap">
                    <div className={style['record-writebox']}>
                        <div className={style['date-record']}>
                            <p>측정 일자</p>
                            <div className='input-box' data-type='c' ref={dayRef} tabIndex={0}>
                                <input type="button" inputMode="none" placeholder='25.02.07'
                                    value={recordDate ? dayjs(recordDate).format('YY.MM.DD') : ''}
                                    onClick={() => setOpenRecordDate(true)}
                                    className={dateInputFocused ? 'focused' : ''}
                                    ref={focusDateInputRef} readOnly />
                            </div>
                        </div>
                        <div className={style['time-record']}>
                            <p>측정 시간</p>
                            <div className='input-box' data-type='c' ref={timeRef} tabIndex={0}>
                                <input type="button" inputMode="none" placeholder='오후 01:30'
                                    value={recordTime}
                                    onClick={() => setOpenRecordTime(true)}
                                    className={timeInputFocused ? 'focused' : ''}
                                    ref={focusTimeInputRef} readOnly />
                            </div>
                        </div>
                        <div>
                            <p>키</p>
                            <div className='input-box' data-type='c'>
                                <input type="number" 
                                    placeholder='---' 
                                    ref={statureRef}
                                    aria-label='키를 입력해주세요' 
                                    step="0.1"
                                    min="0"
                                    max="999.9"
                                    value={stature}
                                    onChange={(e) => {
                                        let value = e.target.value;

                                        // 소수 첫째자리까지만 허용
                                        if (!/^\d{0,3}(\.\d{0,1})?$/.test(value)) {
                                            return;
                                        }

                                        setStature(value);
                                    }}
                                    />
                                <span className='unit'>cm</span>
                            </div>
                        </div>
                        <div>
                            <p>체중</p>
                            <div className='input-box' data-type='c'>
                                <input type="number" 
                                    placeholder='---' 
                                    ref={weightRef}
                                    aria-label='체중을 입력해주세요' 
                                    step="0.1"
                                    min="0"
                                    max="999.9"
                                    value={weight}
                                    onChange={(e) => {
                                        let value = e.target.value;

                                        if (!/^\d{0,3}(\.\d{0,1})?$/.test(value)) {
                                            return;
                                        }

                                        setWeight(value);
                                    }}
                                    />
                                <span className='unit'>kg</span>
                            </div>
                        </div>

                        <BottomSheetBase open={openRecordDate} onDismiss={handleDismissDate}>
                            <CustomDatePicker titleTxt="측정 일자 선택" onClose={handleDismissDate} onConfirm={handleDateConfirm} />
                        </BottomSheetBase>

                        <BottomSheetBase open={openRecordTime} onDismiss={handleDismissTime}>
                            <CustomTimePicker titleTxt="측정 시간 선택" onClose={handleDismissTime} onConfirm={handleConfirmTime} />
                        </BottomSheetBase>
                    </div>

                    <div className="bottom-fixed-box">
                        <div className="inner-box">
                            <div className='btn-layout' data-btn-num="1">
                                <button className="btn" data-btn-type="fill-main" data-btn-size="lg"
                                    onClick={handleComplete} disabled={!stature || !weight || !recordDate || !recordTime}>
                                    <span>완료</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {toast && <ToastPopup setToast={setToast} message={toastMessage} opacity="90" />}
                </div>
            </div>

            <Modal isOpen={modalOpen} setIsOpen={setModalOpen}>
                <div className='modal-box' data-type="a">
                    <div className='modal-top-box'><div className='title-box'><p>오늘 날짜보다 미래 날짜를 선택할 수 없습니다.</p></div></div>
                    <div className="modal-bottom-box"><div className='btn-layout'><button className="btn" data-btn-type="fill-gray" data-btn-size="md" onClick={closeModal}><span>확인</span></button></div></div>
                </div>
            </Modal>

            <Modal isOpen={modalTimeOpen} setIsOpen={setModalTimeOpen}>
                <div className='modal-box' data-type="a">
                    <div className='modal-top-box'><div className='title-box'><p>현재 시간보다 미래 시간을 선택할 수 없습니다.</p></div></div>
                    <div className="modal-bottom-box"><div className='btn-layout'><button className="btn" data-btn-type="fill-gray" data-btn-size="md" onClick={closeModalTime}><span>확인</span></button></div></div>
                </div>
            </Modal>
        </>
    );
};

export default BodyMassWrite;